/**
 * Dashboard empty-state banner: shown when the server has a GitHub App
 * configured but the signed-in user has no installations linked yet.
 * The single job of this component is discoverability — telling users the
 * per-repo, read-only connect flow exists before they open the project form.
 */
import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { rememberGithubReturnPath } from './returnPath';

const DISMISS_KEY = 'github-connect-banner-dismissed';

export function GithubConnectBanner() {
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    let cancelled = false;
    (async () => {
      try {
        const info = await api.getGithubAppInfo();
        if (!info.configured || !info.installUrl) return;
        const { installations } = await api.listGithubInstallations();
        if (!cancelled && installations.length === 0) {
          setInstallUrl(info.installUrl);
          setVisible(true);
        }
      } catch {
        // Not signed in or endpoint unavailable — stay hidden.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible || !installUrl) return null;

  return (
    <div className="mb-6 flex items-start justify-between gap-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <svg className="mt-0.5 h-5 w-5 shrink-0 text-primary" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
        </svg>
        <div>
          <p className="text-sm font-medium">Connect your GitHub repositories</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Analyze private repos by granting <span className="font-medium">read-only</span> access to
            just the repositories you choose — you pick them on GitHub, and you can change the list any
            time. Requires signing in with GitHub.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <a
          href={installUrl}
          onClick={() => rememberGithubReturnPath('/dashboard')}
          className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Connect repositories
        </a>
        <button
          onClick={() => { localStorage.setItem(DISMISS_KEY, '1'); setVisible(false); }}
          className="rounded p-1 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
